import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2, Upload, FileText, Save } from 'lucide-react';
import { toast } from 'sonner';

const CONTRACT_TYPES = [
  { value: 'managed_office', label: 'Managed Office' },
  { value: 'coworking', label: 'Coworking' },
  { value: 'virtual_office', label: 'Virtual Office' },
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'renewed', label: 'Renewed' },
];

const initialForm = {
  client_name: '',
  contract_type: 'managed_office',
  agreement_execution_date: '',
  license_commencement_date: '',
  license_tenure_months: '',
  lock_in_period_months: '',
  notice_period_months: '',
  monthly_license_fee: '',
  number_of_seats: '',
  annual_escalation_pct: '',
  security_deposit_amount: '',
  security_deposit_months: '',
  parking_slots_count: '',
  parking_fee_per_slot: '',
  meeting_room_credits_per_month: '',
  gst_applicable: true,
  additional_customization_cost: '',
  customization_details: '',
  additional_internet_cost: '',
  additional_internet_details: '',
  centre_id: '',
  building_name: '',
  floor_number: '',
  cabin_numbers: '',
  document_storage_path: '',
  document_filename: '',
  status: 'active',
};

const addMonths = (dateStr, months) => {
  if (!dateStr || !months) return '';
  const m = parseInt(months);
  if (isNaN(m) || m <= 0) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + m);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d.toISOString().split('T')[0];
};

const addDays = (dateStr, days) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

export const ContractFormModal = ({ open, onClose, api, venues, editing, renewingFrom, onSaved }) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editing) {
      const merged = { ...initialForm };
      Object.keys(initialForm).forEach((k) => {
        merged[k] = editing[k] ?? initialForm[k];
      });
      setForm(merged);
    } else if (renewingFrom) {
      // Carry over the previous term's details, but require fresh dates and a fresh document.
      const merged = { ...initialForm };
      Object.keys(initialForm).forEach((k) => {
        if (k === 'document_storage_path' || k === 'document_filename' || k === 'status') return;
        merged[k] = renewingFrom[k] ?? initialForm[k];
      });
      merged.agreement_execution_date = '';
      merged.license_commencement_date = addDays(renewingFrom.license_end_date, 1);
      setForm(merged);
    } else {
      setForm(initialForm);
    }
  }, [editing, renewingFrom, open]);

  const licenseEnd = addMonths(form.license_commencement_date, form.license_tenure_months);
  const lockinEnd = addMonths(form.license_commencement_date, form.lock_in_period_months);
  const fee = parseFloat(form.monthly_license_fee) || 0;
  const seats = parseInt(form.number_of_seats) || 0;
  const avgPerSeat = seats > 0 ? (fee / seats).toFixed(2) : '0.00';

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!allowed.includes(file.type) && !['pdf', 'doc', 'docx'].includes(ext)) {
      toast.error('Only PDF, DOC, or DOCX allowed');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/contracts/upload-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((p) => ({
        ...p,
        document_storage_path: res.data.storage_path,
        document_filename: res.data.filename,
      }));
      toast.success(`Uploaded: ${res.data.filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    const required = [
      ['client_name', 'Client Name'],
      ['contract_type', 'Contract Type'],
      ['agreement_execution_date', 'Agreement Execution Date'],
      ['license_commencement_date', 'License Commencement Date'],
      ['license_tenure_months', 'License Tenure'],
      ['lock_in_period_months', 'Lock-in Period'],
      ['notice_period_months', 'Notice Period'],
      ['monthly_license_fee', 'Monthly License Fee'],
      ['number_of_seats', 'Number of Seats'],
      ['centre_id', 'Centre'],
    ];
    for (const [k, label] of required) {
      if (form[k] === '' || form[k] === null || form[k] === undefined) {
        toast.error(`${label} is required`);
        return;
      }
    }
    if (!editing && !form.document_storage_path) {
      toast.error('Please upload the contract document (PDF/DOC)');
      return;
    }
    if (parseInt(form.lock_in_period_months) > parseInt(form.license_tenure_months)) {
      toast.error('Lock-in period cannot exceed license tenure');
      return;
    }

    const payload = {
      ...form,
      license_tenure_months: parseInt(form.license_tenure_months),
      lock_in_period_months: parseInt(form.lock_in_period_months),
      notice_period_months: parseInt(form.notice_period_months),
      monthly_license_fee: parseFloat(form.monthly_license_fee),
      number_of_seats: parseInt(form.number_of_seats),
      annual_escalation_pct: parseFloat(form.annual_escalation_pct) || 0,
      security_deposit_amount: parseFloat(form.security_deposit_amount) || 0,
      security_deposit_months: parseFloat(form.security_deposit_months) || 0,
      parking_slots_count: parseInt(form.parking_slots_count) || 0,
      parking_fee_per_slot: parseFloat(form.parking_fee_per_slot) || 0,
      meeting_room_credits_per_month: parseFloat(form.meeting_room_credits_per_month) || 0,
      additional_customization_cost: parseFloat(form.additional_customization_cost) || 0,
      additional_internet_cost: parseFloat(form.additional_internet_cost) || 0,
    };

    setSaving(true);
    try {
      if (renewingFrom?.id) {
        await api.post(`/contracts/${renewingFrom.id}/renew`, payload);
        toast.success('Contract renewed');
      } else if (editing?.id) {
        await api.patch(`/contracts/${editing.id}`, payload);
        toast.success('Contract updated');
      } else {
        await api.post('/contracts', payload);
        toast.success('Contract created');
      }
      onSaved && onSaved();
      onClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contract');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="contract-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <FileText className="w-5 h-5 text-primary" />
            {editing ? 'Edit Contract' : renewingFrom ? 'Renew Contract' : 'New Contract'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {renewingFrom && (
            <div className="text-sm bg-primary/10 border border-primary/30 rounded-md px-3 py-2" data-testid="contract-renew-banner">
              Renewing <span className="font-semibold">{renewingFrom.client_name}</span>'s contract
              (previous term ended <span className="font-mono">{renewingFrom.license_end_date}</span>).
              Please re-enter all details and upload the newly signed document — this will create a new
              contract record linked to the original.
            </div>
          )}
          {/* Client Info */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Client Info</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client Name (Contract Name) *</Label>
                <Input value={form.client_name} onChange={setField('client_name')} data-testid="contract-client-name" />
              </div>
              <div>
                <Label>Contract Type *</Label>
                <Select value={form.contract_type} onValueChange={setField('contract_type')}>
                  <SelectTrigger data-testid="contract-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Agreement Details */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Agreement Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Agreement Execution Date *</Label>
                <Input type="date" value={form.agreement_execution_date} onChange={setField('agreement_execution_date')} data-testid="contract-execution-date" />
              </div>
              <div>
                <Label>License Commencement Date *</Label>
                <Input type="date" value={form.license_commencement_date} onChange={setField('license_commencement_date')} data-testid="contract-commencement-date" />
              </div>
              <div>
                <Label>License Tenure (Months) *</Label>
                <Input type="number" min={1} value={form.license_tenure_months} onChange={setField('license_tenure_months')} data-testid="contract-tenure" />
              </div>
              <div>
                <Label>Lock-in Period (Months) *</Label>
                <Input type="number" min={1} value={form.lock_in_period_months} onChange={setField('lock_in_period_months')} data-testid="contract-lockin" />
              </div>
              <div>
                <Label>Notice Period (Months) *</Label>
                <Input type="number" min={0} value={form.notice_period_months} onChange={setField('notice_period_months')} data-testid="contract-notice" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-md">
              <div>
                <Label className="text-xs text-muted-foreground">License End Date (auto)</Label>
                <p className="font-mono text-sm" data-testid="contract-license-end">{licenseEnd || '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Lock-in Expiry Date (auto)</Label>
                <p className="font-mono text-sm" data-testid="contract-lockin-expiry">{lockinEnd || '—'}</p>
              </div>
            </div>
          </section>

          {/* Commercial Details */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Commercial Details</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Monthly License Fee (₹) *</Label>
                <Input type="number" min={0} value={form.monthly_license_fee} onChange={setField('monthly_license_fee')} data-testid="contract-fee" />
              </div>
              <div>
                <Label>Number of Seats *</Label>
                <Input type="number" min={1} value={form.number_of_seats} onChange={setField('number_of_seats')} data-testid="contract-seats" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Avg. Per Seat Cost (auto)</Label>
                <p className="font-mono text-sm pt-2" data-testid="contract-avg-seat">₹{avgPerSeat}</p>
              </div>
              <div>
                <Label>Annual Escalation (%)</Label>
                <Input type="number" min={0} step="0.1" value={form.annual_escalation_pct} onChange={setField('annual_escalation_pct')} />
              </div>
              <div>
                <Label>Security Deposit Amount (₹)</Label>
                <Input type="number" min={0} value={form.security_deposit_amount} onChange={setField('security_deposit_amount')} />
              </div>
              <div>
                <Label>Security Deposit (Months)</Label>
                <Input type="number" min={0} step="0.5" value={form.security_deposit_months} onChange={setField('security_deposit_months')} />
              </div>
              <div>
                <Label>Parking Slots Count</Label>
                <Input type="number" min={0} value={form.parking_slots_count} onChange={setField('parking_slots_count')} />
              </div>
              <div>
                <Label>Parking Fee per Slot (₹)</Label>
                <Input type="number" min={0} value={form.parking_fee_per_slot} onChange={setField('parking_fee_per_slot')} />
              </div>
              <div>
                <Label>Meeting Room Credits / Month</Label>
                <Input type="number" min={0} value={form.meeting_room_credits_per_month} onChange={setField('meeting_room_credits_per_month')} />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={!!form.gst_applicable} onCheckedChange={(v) => setForm((p) => ({ ...p, gst_applicable: v }))} data-testid="contract-gst-toggle" />
              <Label>GST Applicable</Label>
            </div>
          </section>

          {/* Additional Costs */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Additional Costs</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Additional Customization Cost (₹)</Label>
                <Input type="number" min={0} value={form.additional_customization_cost} onChange={setField('additional_customization_cost')} />
              </div>
              <div>
                <Label>Additional Internet Cost (₹)</Label>
                <Input type="number" min={0} value={form.additional_internet_cost} onChange={setField('additional_internet_cost')} />
              </div>
              <div className="col-span-2">
                <Label>Customization Details</Label>
                <Textarea rows={2} value={form.customization_details} onChange={setField('customization_details')} />
              </div>
              <div className="col-span-2">
                <Label>Internet Details</Label>
                <Textarea rows={2} value={form.additional_internet_details} onChange={setField('additional_internet_details')} />
              </div>
            </div>
          </section>

          {/* Property Details */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Property Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Centre *</Label>
                <Select value={form.centre_id} onValueChange={setField('centre_id')}>
                  <SelectTrigger data-testid="contract-centre-select"><SelectValue placeholder="Select Centre" /></SelectTrigger>
                  <SelectContent>
                    {(venues || []).filter((v) => v.is_active !== false).map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Building Name</Label>
                <Input value={form.building_name} onChange={setField('building_name')} />
              </div>
              <div>
                <Label>Floor Number</Label>
                <Input value={form.floor_number} onChange={setField('floor_number')} />
              </div>
              <div>
                <Label>Cabin Numbers</Label>
                <Input value={form.cabin_numbers} onChange={setField('cabin_numbers')} placeholder="e.g., 101, 102, 103" />
              </div>
            </div>
          </section>

          {/* File Upload */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Contract Document {!editing && '*'}</h3>
            {renewingFrom && <p className="text-xs text-muted-foreground -mt-2">A newly signed document is required for the renewed term.</p>}
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={handleFileUpload} className="hidden" data-testid="contract-file-input" />
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background hover:bg-accent text-sm">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading…' : 'Upload PDF / DOC / DOCX'}
                </span>
              </label>
              {form.document_filename && (
                <span className="text-sm text-muted-foreground flex items-center gap-2" data-testid="contract-uploaded-filename">
                  <FileText className="w-4 h-4" /> {form.document_filename}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Max 25 MB. Only PDF, DOC, DOCX allowed.</p>
          </section>

          {editing && (
            <section className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={setField('status')}>
                <SelectTrigger data-testid="contract-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || uploading} data-testid="contract-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {editing ? 'Save Changes' : renewingFrom ? 'Renew Contract' : 'Create Contract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractFormModal;
